import { Enrichment } from '@clearkit/icons';
import { CKLayoutGridAuto } from 'clearkit';
import moment from 'moment';
import { useMemo } from 'react';

import { useDataOverviewQuery } from '~/generated/graphql';
import { useEntitlements } from '~/hooks/account';
import useOnboardingStatus from '~/hooks/account/useOnboardingStatus';

import { HomeDataOverviewCard } from '../HomeDataOverviewCard';
import { HomeHeading } from '../HomeHeading';
import { useCRMLabel } from '../hooks/useCRMLabel';

const TIME_PERIOD = 60;

export const HomeSectionDataOverview = () => {
  const today = moment();
  const start = today.subtract(TIME_PERIOD, 'days').format('YYYY-MM-DD');
  const end = today.format('YYYY-MM-DD');

  const { data, loading: isLoading } = useDataOverviewQuery({
    variables: { start, end },
  });

  const { hasAccessEnrichment } = useEntitlements();
  const { crmLabel } = useCRMLabel();
  const {
    hasConnectors,
    connectorNames,
    hasClearbitJs,
    hasGoogleTagManager,
  } = useOnboardingStatus();

  const shouldShowData = hasConnectors;
  const shouldShowVisitors = hasClearbitJs || hasGoogleTagManager;

  const {
    visitorsTotalUniques,
    enrichmentsByDomainTotalUniques,
    enrichmentsByEmailTotalUniques,
  } = useMemo(() => {
    return {
      visitorsTotalUniques: data?.rollupCounts?.visitorsTotalUniques || 0,
      enrichmentsByDomainTotalUniques:
        data?.rollupCounts?.enrichmentsByDomainTotalUniques || 0,
      enrichmentsByEmailTotalUniques:
        data?.rollupCounts?.enrichmentsByEmailTotalUniques || 0,
    };
  }, [data]);

  const Tooltip = ({ type }: { type: 'companies' | 'people' | 'visitors' }) => {
    let message = `Unique ${type} visiting your website`;

    if (hasConnectors) {
      message = `Unique ${type} added from ${crmLabel} or as visitors to your website that are available for enrichment`;
    }

    return <span>{message}</span>;
  };

  const TooltipEnriched = ({ type }: { type: 'person' | 'company' }) => {
    const hasHubspot = connectorNames.includes('hubspot');
    const hasSalesforce = connectorNames.includes('salesforce');

    let service;

    if (hasHubspot && hasSalesforce) {
      service = 'HubSpot and Salesforce';
    } else if (hasHubspot) {
      service = 'HubSpot';
    } else if (hasSalesforce) {
      service = 'Salesforce';
    } else {
      service = crmLabel;
    }

    return (
      <span>
        Any {type} record that’s had new data appended to the record and synced
        to {service}
      </span>
    );
  };

  return (
    <section className="w-full grid gap-8 xl:gap-12">
      <div className="space-y-6">
        <HomeHeading>
          <Enrichment />
          Data Overview
        </HomeHeading>
        <CKLayoutGridAuto>
          {hasAccessEnrichment ? (
            <>
              <HomeDataOverviewCard
                id="companies"
                isLoading={isLoading}
                overviewTotal={enrichmentsByDomainTotalUniques}
                shouldShowData={shouldShowData}
                title={`Companies enriched in ${crmLabel}`}
                variant="purple"
              >
                <HomeDataOverviewCard.Tooltip>
                  <TooltipEnriched type="company" />
                </HomeDataOverviewCard.Tooltip>
              </HomeDataOverviewCard>
              <HomeDataOverviewCard
                id="people"
                isLoading={isLoading}
                overviewTotal={enrichmentsByEmailTotalUniques}
                shouldShowData={shouldShowData}
                title={`People enriched in ${crmLabel}`}
                variant="green"
              >
                <HomeDataOverviewCard.Tooltip>
                  <TooltipEnriched type="person" />
                </HomeDataOverviewCard.Tooltip>
              </HomeDataOverviewCard>
            </>
          ) : null}
          {shouldShowVisitors ? (
            <HomeDataOverviewCard
              id="visitors"
              isLoading={isLoading}
              overviewTotal={visitorsTotalUniques}
              shouldShowData={shouldShowData}
              title="Visitors to your website"
              variant="blue"
            >
              <HomeDataOverviewCard.Tooltip>
                <Tooltip type="visitors" />
              </HomeDataOverviewCard.Tooltip>
            </HomeDataOverviewCard>
          ) : null}
        </CKLayoutGridAuto>
      </div>
    </section>
  );
};
